import { FileSignature, Info, Loader2, Plus, Puzzle, Trash2, Wrench } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Field, FormSection } from '@/components/common/FormBits'
import { Button } from '@/components/ui/button'
import { DateField } from '@/components/ui/date-field'
import { Input } from '@/components/ui/input'
import { Modal } from '@/components/ui/modal'
import { NumberInput } from '@/components/ui/number-input'
import { Select } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { useProject } from '@/features/projects/api'
import { fmtMoney } from '../../../shared/format'
import { useMyDiscountLimit } from '../../api'
import { useSaveQuote, type QuoteLineInput } from '../../quotes'
import type { Offer } from '../../types'
import { toastError, useCrm } from '../../ui'

type Line = QuoteLineInput & { key: string }
const today = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Istanbul' })
const plus30 = () => {
  const d = new Date()
  d.setDate(d.getDate() + 30)
  return d.toLocaleDateString('sv-SE', { timeZone: 'Europe/Istanbul' })
}
const PRICE = /^\d+([.,]\d{1,2})?$/

/**
 * Teklif formu: proje (taslak proje dahil) seçilir, kalemler projenin modüllerinden; modül dışı iş "ek iş kalemi" olarak
 * açıklamasıyla eklenir. Kayıtlı teklifte her değişiklik yeni revizyondur ve açıklama ister; onaylı teklif düzenlenince
 * yeniden onaya gider.
 */
export function OfferForm({ customerId, offer, onDone }: { customerId: string; offer: Offer | null; onDone: () => void }) {
  const { t } = useTranslation()
  const { projects, customerById } = useCrm()
  const save = useSaveQuote()
  const limit = useMyDiscountLimit(customerId)
  const [projectId, setProjectId] = useState(offer?.projectId || customerById.get(customerId)?.projectId || '')
  const project = useProject(projectId || null)
  const modules = useMemo(() => project.data?.modules ?? [], [project.data])
  const [date, setDate] = useState(offer?.date || today())
  const [validUntil, setValidUntil] = useState(offer?.validUntil || plus30())
  const [discountPct, setDiscountPct] = useState(String(offer?.discountPct ?? 0))
  const [note, setNote] = useState(offer?.note ?? '')
  const [reason, setReason] = useState('')
  const [lines, setLines] = useState<Line[]>(
    (offer?.items ?? []).map((i) => ({
      key: i.id,
      lineType: i.lineType === 'module' || i.lineType === 'feature' ? 'module' : 'extra',
      refCode: i.refCode,
      description: i.lineType === 'extra' ? i.description : i.lineType === 'module' ? '' : i.name,
      qty: i.qty,
      unitPrice: i.unitPrice,
      discount: i.discount || '0',
    })),
  )
  const [pick, setPick] = useState('')
  const patch = (key: string, p: Partial<Line>) => setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...p } : l)))
  const moduleName = (code: string) => modules.find((m) => m.code === code)?.name ?? code
  const used = new Set(lines.filter((l) => l.lineType === 'module').map((l) => l.refCode))
  const available = modules.filter((m) => !used.has(m.code))
  const invalidLine = lines.some((l) => !PRICE.test(l.unitPrice.trim() || 'x') || l.qty < 1 || (l.lineType === 'extra' && l.description.trim().length < 3))
  const pct = Number(discountPct.replace(',', '.'))
  const invalid = !lines.length || invalidLine || !(pct >= 0 && pct <= 100) || validUntil < date || (!!offer && reason.trim().length < 3)
  const preview = lines.reduce((n, l) => n + Math.max(0, Number(l.unitPrice.replace(',', '.')) * l.qty - Number(l.discount.replace(',', '.') || 0)), 0) * (1 - (pct || 0) / 100)

  const submit = () => {
    if (invalid) return
    save.mutate(
      { id: offer?.id, input: { customerId, projectId, date, validUntil, discountPct, note, reason, lines } },
      {
        onSuccess: () => {
          toast.success(t(offer ? 'crm.quote.revised' : 'crm.offer.created'))
          onDone()
        },
        onError: toastError,
      },
    )
  }

  return (
    <Modal
      open
      size="2xl"
      onOpenChange={(v) => !v && onDone()}
      dismissible={!save.isPending}
      icon={<FileSignature />}
      title={offer ? t('crm.quote.reviseTitle', { number: offer.number, rev: offer.revision + 1 }) : t('crm.offer.createTitle')}
      description={offer && offer.currentApproved ? t('crm.quote.reviseApprovedHint') : t('crm.quote.formHint')}
      footer={
        <>
          <Button type="button" variant="cancel" onClick={onDone}>
            {t('common.cancel')}
          </Button>
          <Button onClick={submit} disabled={invalid || save.isPending}>
            {save.isPending ? <Loader2 className="animate-spin" /> : null}
            {offer ? t('crm.quote.saveRevision') : t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-6">
        <FormSection title={t('crm.offer.sectionGeneral')}>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field id="of-project" label={t('crm.quote.project')} hint={t('crm.quote.projectHint')} full>
              <Select
                id="of-project"
                searchable
                value={projectId}
                onChange={setProjectId}
                options={[{ value: '', label: t('crm.quote.noProject') }, ...projects.map((p) => ({ value: p.id, label: p.name }))]}
              />
            </Field>
            <Field id="of-date" label={t('crm.offer.date')}>
              <DateField id="of-date" value={date} onChange={setDate} clearable={false} />
            </Field>
            <Field id="of-valid" label={t('crm.offer.validUntil')} error={validUntil < date ? t('crm.offer.validInvalid') : undefined}>
              <DateField id="of-valid" value={validUntil} onChange={setValidUntil} clearable={false} />
            </Field>
            <Field
              id="of-disc"
              label={t('crm.offer.discount')}
              hint={limit.data && !limit.data.override && limit.data.maxPercent != null ? t('crm.quote.limitHint', { max: limit.data.maxPercent.toFixed(2) }) : undefined}
            >
              <Input id="of-disc" inputMode="decimal" value={discountPct} onChange={(e) => setDiscountPct(e.target.value)} />
            </Field>
          </div>
        </FormSection>

        <fieldset className="space-y-3">
          <legend className="mb-3 w-full border-b pb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('crm.offer.items')}</legend>
          {lines.map((l) => (
            <div key={l.key} className="grid grid-cols-2 gap-2 rounded-lg border p-2 sm:grid-cols-[minmax(0,1fr)_5rem_9rem_8rem_2.25rem] sm:items-center">
              <div className="col-span-2 flex min-w-0 items-center gap-2 sm:col-span-1">
                {l.lineType === 'module' ? <Puzzle className="size-4 shrink-0 text-primary" aria-hidden /> : <Wrench className="size-4 shrink-0 text-warning" aria-hidden />}
                {l.lineType === 'module' ? (
                  <span className="truncate text-sm font-medium">{moduleName(l.refCode)}</span>
                ) : (
                  <Input aria-label={t('crm.quote.extraDescription')} placeholder={t('crm.quote.extraDescription')} value={l.description} maxLength={500} onChange={(e) => patch(l.key, { description: e.target.value })} />
                )}
              </div>
              <Input aria-label={t('crm.offer.qty')} type="number" min={1} value={l.qty} onChange={(e) => patch(l.key, { qty: Math.max(1, Number(e.target.value) || 1) })} />
              <NumberInput aria-label={t('crm.offer.unitPrice')} value={l.unitPrice} onChange={(unitPrice) => patch(l.key, { unitPrice })} minDecimalsOnBlur={2} suffix="₺" />
              <NumberInput aria-label={t('crm.quote.lineDiscount')} placeholder={t('crm.quote.lineDiscount')} value={l.discount === '0' ? '' : l.discount} onChange={(discount) => patch(l.key, { discount: discount || '0' })} minDecimalsOnBlur={2} suffix="₺" />
              <Button type="button" variant="ghost" size="icon" aria-label={t('crm.offer.removeItem')} onClick={() => setLines((ls) => ls.filter((x) => x.key !== l.key))}>
                <Trash2 />
              </Button>
            </div>
          ))}
          <div className="flex flex-wrap items-center gap-2">
            <Select
              className="w-72"
              aria-label={t('crm.quote.addModule')}
              value={pick}
              disabled={!available.length}
              onChange={(code) => {
                const m = modules.find((x) => x.code === code)
                if (m) setLines((ls) => [...ls, { key: crypto.randomUUID(), lineType: 'module', refCode: m.code, description: '', qty: 1, unitPrice: Number(m.price || 0).toFixed(2), discount: '0' }])
                setPick('')
              }}
              options={[{ value: '', label: projectId ? (available.length ? t('crm.quote.addModule') : t('crm.quote.noModules')) : t('crm.quote.pickProject') }, ...available.map((m) => ({ value: m.code, label: `${m.name} · ${fmtMoney(m.price || '0')}` }))]}
            />
            <Button type="button" variant="outline" size="sm" onClick={() => setLines((ls) => [...ls, { key: crypto.randomUUID(), lineType: 'extra', refCode: '', description: '', qty: 1, unitPrice: '', discount: '0' }])}>
              <Plus />
              {t('crm.quote.addExtra')}
            </Button>
          </div>
          {invalidLine ? <p className="text-xs text-destructive">{t('crm.quote.lineInvalid')}</p> : null}
          {!lines.length ? <p className="text-xs text-muted-foreground">{t('crm.offer.itemsRequired')}</p> : null}
        </fieldset>

        <FormSection>
          <Field id="of-note" label={t('crm.offer.note')} full>
            <Input id="of-note" maxLength={400} value={note} onChange={(e) => setNote(e.target.value)} />
          </Field>
          {offer ? (
            <Field id="of-reason" label={t('crm.quote.reason')} hint={t('crm.quote.reasonHint')} full error={reason.trim().length < 3 ? t('crm.quote.reasonRequired') : undefined}>
              <Textarea id="of-reason" rows={2} maxLength={1000} value={reason} onChange={(e) => setReason(e.target.value)} />
            </Field>
          ) : null}
          <p className="flex items-start gap-2 text-xs text-muted-foreground sm:col-span-2">
            <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden />
            {t('crm.quote.previewTotal', { total: fmtMoney(preview.toFixed(2)) })}
          </p>
        </FormSection>
      </div>
    </Modal>
  )
}
